import React, { useEffect, useState } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import { PrevBtn, NextBtn, PaginationWrapper } from "../Styles/PageStyles";
import Images from "../../Images";
import tw from "tailwind-styled-components";
import { PaginationLimit } from "../../Config";
import moment from "moment";
import { truncateText } from "../../helpers";

const STAFF_LEDGER_TYPES = {
    CASH: 1,
    EXPENSES: 2,
    PAY_OUT: 3,
    SALARY: 4,
    EXPENSE_RETURN: 5,
    BONUS: 6,
    REIMBURSEMENT: 7,
    SALARY_ADVANCE: 8,
    TRAVEL_ALLOWANCE: 9,
    HRA: 10,
    COMMISSION: 11,
};

const Table = ({ apiData }) => {
    const [data, setData] = useState([]);
    const [selectedRow, setSelectedRow] = useState(null);
    const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
    const [currentPage, setCurrentPage] = useState(0);

    const getTypeLabel = (type) => {
        const types = {
            [STAFF_LEDGER_TYPES.CASH]: "Cash",
            [STAFF_LEDGER_TYPES.EXPENSES]: "Expenses",
            [STAFF_LEDGER_TYPES.PAY_OUT]: "Pay Out",
            [STAFF_LEDGER_TYPES.SALARY]: "Salary",
            [STAFF_LEDGER_TYPES.EXPENSE_RETURN]: "Expense Return",
            [STAFF_LEDGER_TYPES.BONUS]: "Bonus",
            [STAFF_LEDGER_TYPES.REIMBURSEMENT]: "Reimbursement",
            [STAFF_LEDGER_TYPES.SALARY_ADVANCE]: "Salary Advance",
            [STAFF_LEDGER_TYPES.TRAVEL_ALLOWANCE]: "Travel Allowance",
            [STAFF_LEDGER_TYPES.HRA]: "HRA",
            [STAFF_LEDGER_TYPES.COMMISSION]: "Commission",
        };
        return types[type] || "Unknown";
    };

    useEffect(() => {
        if (apiData && Array.isArray(apiData)) {
            const tempData = apiData.map((item) => ({
                // amount: '₹' + (item.amount),
                amount: getAmountDisplay(item.amount),
                type: getType(item),
                createDate: createDate(item),
                // description: getDescription(item),
                description: item?.description || '',
                original: item,
            }));
            setData(tempData);
        }
    }, [apiData]);

    const getAmountDisplay = (amount) => {
        const numAmount = Number(amount);
        const formattedAmount = Math.abs(numAmount).toLocaleString();

        if (numAmount < 0) {
            return (
                <MinusAmount>
                    ₹{formattedAmount}
                </MinusAmount>
            );
        } else {
            return (
                <Amount>
                    ₹{formattedAmount}
                </Amount>
            );
        }
    };

    const getDescription = (item) => {
        return (
            <span className="text-gray-600 text-sm">
                {item?.description ? truncateText(item?.description, 20) : "N/A"}
            </span>
        );
    };

    const getType = (item) => {
        return (
            <span className="text-gray-600 text-sm">
                {getTypeLabel(item.type)}
            </span>
        );
    };

    const createDate = (item) => {
        return (
            <div className="flex flex-col text-sm text-gray-600">
                <span>
                    {item.createdAt ? moment(item.createdAt).format("DD, MMM YY") : "---"}
                </span>
            </div>
        );
    };

    useEffect(() => {
        const handleResize = () => {
            setIsMobileView(window.innerWidth < 768);
        };

        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const columns = React.useMemo(() => {
        if (isMobileView) {
            return [
                {
                    Header: "Date",
                    accessor: "createdAt",
                },
                {
                    Header: "Amount",
                    accessor: "amount",
                },
            ];
        }
        return [
            {
                Header: "Date",
                accessor: "createDate",
            },
            {
                Header: "Type",
                accessor: "type",
            },
            {
                Header: "Amount",
                accessor: "amount",
            },
            {
                Header: "Desc",
                accessor: "description",
            },
        ];
    }, [isMobileView]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        pageOptions,
        page,
        state: { pageIndex },
        previousPage,
        nextPage,
        canPreviousPage,
        canNextPage,
    } = useTable(
        {
            data,
            columns,
            initialState: { pageIndex: currentPage, pageSize: PaginationLimit },
        },
        useSortBy,
        usePagination,
    );

    useEffect(() => {
        setCurrentPage(pageIndex);
    }, [pageIndex]);

    const toggleRow = (index) => {
        setSelectedRow(selectedRow === index ? null : index);
    };

    return (
        <>
            <CustomTable {...getTableProps()}>
                <Thead>
                    {headerGroups.map((headerGroup) => (
                        <Tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                                <Th
                                    {...column.getHeaderProps(column.getSortByToggleProps())}
                                    style={{ width: column.width }}
                                >
                                    {column.render("Header")}
                                    <span>
                                        {column.isSorted ? (
                                            <img
                                                src={Images.Arrow}
                                                alt="down arrow"
                                                className={`${column.isSortedDesc ? "-rotate-90" : "rotate-90"
                                                    } w-1.5 inline-block ml-1.5`}
                                            />
                                        ) : (
                                            ""
                                        )}
                                    </span>
                                </Th>
                            ))}
                        </Tr>
                    ))}
                </Thead>
                <Tbody
                    {...getTableBodyProps()}
                    hasdata={(apiData?.length > 0).toString()}
                >
                    {page.map((row, rowIndex) => {
                        prepareRow(row);
                        return (
                            <React.Fragment key={rowIndex}>
                                <Tr
                                    {...row.getRowProps({ style: { height: "80px" } })}
                                    hasdata={(apiData?.length > 0).toString()}
                                >
                                    {row.cells.map((cell, cellIndex) => {
                                        return (
                                            <Td
                                                {...cell.getCellProps()}
                                                onClick={() => {
                                                    if (
                                                        cellIndex === 0 ||
                                                        (cellIndex === 1 && window.innerWidth < 768)
                                                    ) {
                                                        toggleRow(rowIndex);
                                                    }
                                                }}
                                            >
                                                {typeof cell.value === "function"
                                                    ? cell.value() // render dynamic function
                                                    : cell.render("Cell")}
                                            </Td>
                                        );
                                    })}
                                </Tr>
                                {selectedRow === rowIndex && isMobileView && (
                                    <Tr>
                                        <Td colSpan={columns.length}>
                                            <ul className="flex flex-col gap-2 ">
                                                <li>
                                                    <strong>Description</strong>{" "}
                                                    {data[rowIndex].description}
                                                </li>
                                                <li>
                                                    <strong>Type</strong> {data[rowIndex].type}
                                                </li>
                                            </ul>
                                        </Td>
                                    </Tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </Tbody>
            </CustomTable>
            {apiData?.length > 0 && (
                <PaginationWrapper>
                    <div className="px-2">
                        Page{" "}
                        <em>
                            {pageIndex + 1} of {pageOptions.length}
                        </em>
                    </div>

                    <div className="flex gap-1">
                        <PrevBtn onClick={() => previousPage()} disabled={!canPreviousPage}>
                            <img src={Images.Arrow} alt="arrow" className="w-2 opacity-75 " />
                        </PrevBtn>
                        <NextBtn onClick={() => nextPage()} disabled={!canNextPage}>
                            <img
                                src={Images.Arrow}
                                alt="arrow"
                                className="w-2 rotate-180 opacity-75"
                            />
                        </NextBtn>
                    </div>
                </PaginationWrapper>
            )}
        </>
    );
};

const CustomTable = tw.table` w-full text-zinc-600`;
const Thead = tw.thead`border-b uppercase text-gray-600 bg-blue-50`;
const Tbody = tw.tbody`${(props) => (props.hasdata === "true" ? "border-b" : "")}`;
const Tr = tw.tr`${(props) =>
    props.hasdata === "true"
        ? "border-b"
        : ""} rounded-md overflow-hidden hover:bg-slate-50`;
const Th = tw.th`text-left text-sm md:p-3 p-0.5 font-normal text-gray-600`;
const Td = tw.td`p-3 text-sm md:p-3 text-left`;
const Amount = tw.p`
  text-green-600 font-semibold
  bg-green-50 px-3 py-1
  rounded-full inline-flex
  cursor-pointer
`;
const MinusAmount = tw.p`
  text-rose-600 font-semibold
  bg-rose-50 px-3 py-1
  rounded-full inline-flex
  cursor-pointer
`;

export default Table;
